import { useState, useMemo } from "react";
import { Search, Package, Check, Tag, Loader2, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { Product } from "@shared/schema";
import { getQueryFn } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
// Task #744 — reusable product-variant picker.
import { VariantPicker } from "@/components/variant-picker";

interface ProductStepProps {
  brandId: string;
  selectedProduct: Product | null;
  onSelect: (product: Product) => void;
  // Task #744 — chosen variant name (null = "no specific variant").
  selectedVariantName: string | null;
  onVariantChange: (name: string | null) => void;
}

interface ReviewStats {
  count: number;
  avgRating: number | null;
}

function StarRating({ rating, count }: { rating: number; count: number }) {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.25 && rating - full < 0.75;
  const almostFull = rating - full >= 0.75;
  const stars = Array.from({ length: 5 }, (_, i) => {
    if (i < full || (almostFull && i === full)) return "full";
    if (hasHalf && i === full) return "half";
    return "empty";
  });

  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center gap-0.5">
        {stars.map((type, i) => (
          <Star
            key={i}
            className={`h-3 w-3 ${
              type === "full"
                ? "fill-amber-400 text-amber-400"
                : type === "half"
                  ? "fill-amber-200 text-amber-400"
                  : "fill-muted text-muted-foreground/30"
            }`}
          />
        ))}
      </div>
      <span className="text-[10px] font-medium text-foreground/70">{rating.toFixed(1)}</span>
      <span className="text-[10px] text-muted-foreground">({count.toLocaleString()})</span>
    </div>
  );
}

export function ProductStep({ brandId, selectedProduct, onSelect, selectedVariantName, onVariantChange }: ProductStepProps) {
  const [search, setSearch] = useState("");

  const { data: products = [], isLoading } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    queryFn: getQueryFn({ on401: "returnNull" }) as () => Promise<Product[]>,
    enabled: !!brandId,
  });

  const { data: reviewStats = {} } = useQuery<Record<string, ReviewStats>>({
    queryKey: [`/api/brands/${brandId}/product-review-stats`],
    queryFn: getQueryFn({ on401: "returnNull" }) as () => Promise<Record<string, ReviewStats>>,
    enabled: !!brandId,
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.summary?.toLowerCase().includes(q)
    );
  }, [search, products]);

  return (
    <div className="space-y-6" data-testid="step-product">
      <div>
        <h2 className="text-xl font-bold tracking-tight" data-testid="text-step-title">
          Select Product
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose the product this campaign will promote
        </p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-product-search"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading products...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {search ? "No products match your search" : "No products found for this brand"}
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((product) => {
              const isSelected = selectedProduct?.id === product.id;
              const media = Array.isArray(product.mediaJson) ? (product.mediaJson as string[]) : [];
              const variants = Array.isArray(product.variantsJson) ? (product.variantsJson as { name: string; price?: string }[]) : [];
              const thumbUrl = media[0] || null;
              const description = product.summary || product.description;
              const stats = reviewStats[product.id];
              const hasReviews = stats && stats.count > 0 && stats.avgRating != null;

              return (
                <Card
                  key={product.id}
                  className={`overflow-hidden cursor-pointer transition-all ${
                    isSelected
                      ? "border-primary/40 bg-primary/[0.03] ring-1 ring-primary/20"
                      : "hover-elevate"
                  }`}
                  onClick={() => onSelect(product)}
                  data-testid={`card-product-${product.id}`}
                >
                  <div className="flex">
                    {thumbUrl ? (
                      <div className="w-24 h-24 shrink-0 bg-muted overflow-hidden">
                        <img
                          src={thumbUrl}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 shrink-0 bg-muted/50 flex items-center justify-center">
                        <Package className="h-8 w-8 text-muted-foreground/30" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold" data-testid={`text-product-name-${product.id}`}>
                              {product.name}
                            </h3>
                            {product.category && (
                              <Badge variant="secondary" className="text-[10px]">
                                {product.category}
                              </Badge>
                            )}
                          </div>
                          {description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {description}
                            </p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {product.priceText && (
                          <span className="text-sm font-semibold text-foreground">
                            ${product.priceText}
                          </span>
                        )}
                        {product.productRole && (
                          <Badge variant="outline" className="text-[10px] capitalize">
                            <Tag className="h-2.5 w-2.5 mr-1" />
                            {product.productRole}
                          </Badge>
                        )}
                        {variants.length > 0 && (
                          <span className="text-[10px] text-muted-foreground">
                            {variants.length} variant{variants.length !== 1 ? "s" : ""}
                          </span>
                        )}
                        {hasReviews && (
                          <StarRating rating={stats.avgRating!} count={stats.count} />
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Task #744 — optional variant picker (renders only when the product has variants). */}
        {selectedProduct && (
          <VariantPicker
            product={selectedProduct}
            value={selectedVariantName}
            onChange={onVariantChange}
            testIdPrefix="wizard"
          />
        )}
      </div>
    </div>
  );
}
